"use client";

import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import {
  type ChangeEvent,
  type ComponentPropsWithoutRef,
  forwardRef,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import type { MentionableSearchResult } from "@/actions/mentions";
import { searchMentionables } from "@/actions/mentions";
import { AssistantAvatar } from "@/components/mcp/assistant-avatar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

type TextareaProps = ComponentPropsWithoutRef<typeof Textarea>;

type MentionTextareaProps = {
  value: string;
  onChange: (value: string) => void;
  containerClassName?: string;
} & Omit<TextareaProps, "value" | "onChange" | "onSelect" | "onKeyDown">;

type ActiveMentionQuery = {
  start: number;
  end: number;
  text: string;
};

export const MentionTextarea = forwardRef<
  HTMLTextAreaElement | null,
  MentionTextareaProps
>(function MentionTextarea(props, forwardedRef) {
  const {
    value,
    onChange,
    containerClassName,
    className,
    disabled,
    onBlur,
    ...rest
  } = props;

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const suppressNextSelectRef = useRef(false);

  useEffect(() => {
    if (!forwardedRef) return;
    if (typeof forwardedRef === "function") {
      forwardedRef(textareaRef.current);
      return () => forwardedRef(null);
    }
    forwardedRef.current = textareaRef.current;
    return () => {
      forwardedRef.current = null;
    };
  }, [forwardedRef]);

  const [mentionQuery, setMentionQuery] = useState<ActiveMentionQuery | null>(
    null,
  );
  const suggestionsId = useId();
  const [selectedHandle, setSelectedHandle] = useState("");

  // Mention suggestions via TanStack Query
  const isSuggestionOpen = Boolean(mentionQuery);
  const trimmedQueryText = mentionQuery?.text.trim() ?? "";
  const queryEnabled = isSuggestionOpen;
  const { data: suggestions = [], isFetching } = useQuery<
    MentionableSearchResult[]
  >({
    queryKey: ["mentions", "search", trimmedQueryText] as const,
    enabled: queryEnabled,
    queryFn: async () => {
      const result = await searchMentionables(trimmedQueryText);
      return result.ok ? result.data : [];
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: 15_000,
    gcTime: 5 * 60_000,
    retry: false,
  });

  const hasQueryText = (mentionQuery?.text.trim().length ?? 0) > 0;
  const updateMentionQuery = (nextValue: string, cursor: number) => {
    setMentionQuery(findActiveMention(nextValue, cursor));
    setSelectedHandle("");
  };

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const nextValue = event.target.value;
    onChange(nextValue);
    const nextCursor = event.target.selectionStart ?? nextValue.length;
    updateMentionQuery(nextValue, nextCursor);
  };

  const handleSelect = () => {
    if (suppressNextSelectRef.current) {
      suppressNextSelectRef.current = false;
      return;
    }
    const textarea = textareaRef.current;
    if (!textarea) return;
    const nextCursor = textarea.selectionStart ?? textarea.value.length;
    updateMentionQuery(value, nextCursor);
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (
    event,
  ) => {
    if (!isSuggestionOpen) {
      return;
    }

    if (
      ["ArrowDown", "ArrowUp", "Enter"].includes(event.key) &&
      suggestions.length &&
      !event.nativeEvent.isComposing
    ) {
      event.preventDefault();
      const current = suggestions.findIndex((s) => s.handle === selectedHandle);
      if (event.key === "Enter") {
        applyMention(suggestions[Math.max(0, current)]);
      } else {
        const next =
          event.key === "ArrowDown"
            ? (current + 1) % suggestions.length
            : current <= 0
              ? suggestions.length - 1
              : current - 1;
        setSelectedHandle(suggestions[next].handle);
      }
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      setMentionQuery(null);

      return;
    }
  };

  const applyMention = (suggestion: MentionableSearchResult) => {
    if (!mentionQuery) return;
    const before = value.slice(0, mentionQuery.start);
    const after = value.slice(mentionQuery.end);

    const mentionText =
      suggestion.type === "event" && suggestion.eventSlug
        ? `@${suggestion.handle}/${suggestion.eventSlug}`
        : `@${suggestion.handle}`;
    const needsTrailingSpace = after.length === 0 || after[0] !== " ";
    const nextValue = `${before}${mentionText}${needsTrailingSpace ? " " : ""}${after}`;

    onChange(nextValue);
    setMentionQuery(null);

    suppressNextSelectRef.current = true;
    requestAnimationFrame(() => {
      const textarea = textareaRef.current;
      if (!textarea) {
        suppressNextSelectRef.current = false;
        return;
      }
      const caretPosition =
        before.length + mentionText.length + (needsTrailingSpace ? 1 : 0);
      textarea.focus();
      textarea.setSelectionRange(caretPosition, caretPosition);
      suppressNextSelectRef.current = false;
    });
  };

  return (
    <div className={cn("relative", containerClassName)}>
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onSelect={handleSelect}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-controls={isSuggestionOpen ? suggestionsId : undefined}
        aria-expanded={isSuggestionOpen}
        onBlur={(event) => {
          setMentionQuery(null);
          onBlur?.(event);
        }}
        className={className}
        {...rest}
      />

      {isSuggestionOpen && !disabled && (
        <div
          id={suggestionsId}
          data-vaul-no-drag
          className="absolute bottom-full left-0 z-50 mb-2 w-72 max-w-[calc(100vw-5rem)] overflow-hidden rounded-2xl border border-border bg-surface shadow-xl"
          // Stay inside the sheet's focus scope; tapping a suggestion must not
          // blur the editor or dismiss the sheet before the selection is applied.
          onPointerDown={(event) => event.preventDefault()}
        >
          <Command
            shouldFilter={false}
            value={selectedHandle}
            onValueChange={setSelectedHandle}
          >
            <div className="flex items-center justify-between border-b px-3 py-2 text-xs text-text-secondary">
              <span>Tag someone</span>
              {isFetching && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            </div>
            <CommandList className="max-h-48 overscroll-contain">
              {suggestions.length === 0 && !isFetching ? (
                <CommandEmpty>
                  {!hasQueryText ? "Start typing to search" : "No matches yet"}
                </CommandEmpty>
              ) : (
                <CommandGroup>
                  {suggestions.map((suggestion) => (
                    <CommandItem
                      key={`${suggestion.type}-${suggestion.id}`}
                      value={suggestion.handle}
                      onSelect={() => applyMention(suggestion)}
                      className="flex items-center gap-3"
                    >
                      {suggestion.type === "assistant" ? (
                        <AssistantAvatar
                          ownerName={suggestion.ownerName}
                          ownerAvatarUrl={suggestion.ownerAvatarUrl}
                          className="size-8"
                        />
                      ) : (
                        <Avatar className="h-8 w-8 border border-border">
                          <AvatarImage
                            src={suggestion.avatarUrl ?? undefined}
                          />
                          <AvatarFallback className="text-xs">
                            {fallbackInitials(suggestion.label)}
                          </AvatarFallback>
                        </Avatar>
                      )}
                      <div className="flex flex-1 flex-col text-sm">
                        <span className="font-medium text-text-primary">
                          {suggestion.label}
                        </span>
                        <span className="text-xs text-text-secondary">
                          {suggestion.type === "event" && suggestion.eventSlug
                            ? `@${suggestion.handle}/${suggestion.eventSlug}`
                            : `@${suggestion.handle}`}
                        </span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </div>
      )}
    </div>
  );
});

function findActiveMention(
  value: string,
  cursor: number,
): ActiveMentionQuery | null {
  const beforeCursor = value.slice(0, cursor);
  const atIndex = beforeCursor.lastIndexOf("@");
  if (atIndex === -1) {
    return null;
  }

  if (atIndex > 0) {
    const charBefore = beforeCursor[atIndex - 1];
    if (charBefore && !/\s/.test(charBefore)) {
      return null;
    }
  }

  const queryText = beforeCursor.slice(atIndex + 1);
  if (!/^[^@\n\r]*$/.test(queryText)) {
    return null;
  }
  if (/\s/.test(queryText)) {
    return null;
  }

  return {
    start: atIndex,
    end: cursor,
    text: queryText,
  } satisfies ActiveMentionQuery;
}

function fallbackInitials(label: string) {
  const trimmed = label.trim();
  if (!trimmed) return "";
  const parts = trimmed.split(" ");
  if (parts.length === 1) {
    return parts[0]?.slice(0, 2).toUpperCase();
  }
  return `${parts[0]?.[0] ?? ""}${parts[1]?.[0] ?? ""}`.toUpperCase();
}
